// @ts-nocheck
import React from 'react';
import { Download, Users, DollarSign, CheckCircle, AlertTriangle, Calculator } from 'lucide-react';
import { PageHeader, Card, Button, InfoBanner, Badge, Th, Td, StatCard } from '../../components/WorkflowFormPrimitives';
import { fmt } from '../../utils/formatCurrency';
import { build280gAnalysisResults } from '../../utils/build280gAnalysisResults';
import { isCompensationStepComplete, isBaseAmountStepComplete } from '../../../../utils/compensationStep';
import { hasParachuteProgress } from '../../components/WorkflowStepsPanel';
import { DocumentsTable } from '../documents/DocumentsTable';
import { useCompanyContext } from '../../context/CompanyContext';

export const Analysis = ({
  formData,
  onCalculate,
  isCalculating = false,
  calculateNotice = null,
  lastRunLabel = null,
  isRunStale = false,
  analysisLocked = false,
  snapshotMode = false,
  snapshotResults = null,
  snapshotCapturedAt = null,
  analysisTitle = '280G Results',
  headerAction = null,
}) => {
  const { currentAnalysis } = useCompanyContext();
  const results =
    snapshotMode && Array.isArray(snapshotResults) && snapshotResults.length > 0
      ? snapshotResults
      : build280gAnalysisResults(formData);
  const safeCount = results.filter((r) => r.status === 'under').length;
  const excessCount = results.filter((r) => r.status === 'over').length;
  const totalParachute = results.reduce((s, r) => s + r.parachute, 0);
  const totalExcess = results.reduce((s, r) => s + Math.max(0, -r.difference), 0);
  const afrDisplay = +formData.afr || 5;
  const isFormComplete =
    !!formData?.companyName &&
    (formData?.individuals?.length ?? 0) > 0 &&
    isCompensationStepComplete(formData || {}) &&
    isBaseAmountStepComplete(formData || {}) &&
    hasParachuteProgress(formData || {});
  // If the last run already reflects the latest workflow changes, there is nothing new to "re-run".
  const isRunFresh = !!lastRunLabel && !isRunStale;
  const runDisabled = analysisLocked || isCalculating || !isFormComplete || isRunFresh;
  const shouldMaskResults = !snapshotMode && (!lastRunLabel || isRunStale);

  return (
    <div className="space-y-5">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <span>{analysisTitle || '280G Results'}</span>
            {snapshotMode ? (
              <Badge variant="info">Historical Snapshot</Badge>
            ) : lastRunLabel ? (
              <Badge variant={isRunStale ? 'danger' : 'info'}>Last Run: {lastRunLabel}</Badge>
            ) : null}
          </span>
        }
        subtitle={`${formData.companyName || 'Transaction'} — Present Value Summary`}
        action={
          headerAction != null ? (
            headerAction
          ) : snapshotMode ? null : (
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    if (!runDisabled && typeof onCalculate === 'function') {
                      void onCalculate();
                    }
                  }}
                  disabled={runDisabled}
                  title={
                    analysisLocked
                      ? 'This analysis is final and cannot be edited or re-run.'
                      : !isFormComplete
                        ? 'Complete Transaction, Individuals, Compensation, Base Amount, and Parachute Payments first.'
                        : isRunFresh
                          ? 'Latest run already matches current workflow changes.'
                          : 'Record a 280G run snapshot'
                  }
                >
                  <Calculator size={14} className="mr-1.5" /> {isCalculating ? 'Running…' : 'Run 280G Results'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={runDisabled || isRunStale}
                  title={
                    analysisLocked
                      ? 'This analysis is final and cannot be edited.'
                      : !isFormComplete
                        ? 'Complete Transaction, Individuals, Compensation, Base Amount, and Parachute Payments first.'
                        : isRunStale
                          ? 'Run 280G Results after the latest workflow changes to export.'
                          : 'Export analysis outputs'
                  }
                >
                  <Download size={14} className="mr-1.5" /> Export
                </Button>
              </div>
            </div>
          )
        }
      />
      {snapshotMode && snapshotCapturedAt ? (
        <InfoBanner variant="info" showIcon>
          Snapshot captured at{' '}
          {(() => {
            const d = new Date(String(snapshotCapturedAt));
            if (Number.isNaN(d.getTime())) return String(snapshotCapturedAt);
            return d.toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: 'numeric',
              minute: '2-digit',
            });
          })()}
          . Inputs and results are read-only.
        </InfoBanner>
      ) : null}
      {!snapshotMode && !isFormComplete ? (
        <InfoBanner variant="warning" showIcon>
          Complete Transaction, Individuals, Compensation, Base Amount, and Parachute Payments to run 280G results.
        </InfoBanner>
      ) : null}
      {!snapshotMode && calculateNotice ? (
        <InfoBanner variant={calculateNotice.variant} showIcon>
          {calculateNotice.text}
        </InfoBanner>
      ) : null}
      <InfoBanner>
        <strong>Present Value Basis:</strong> All amounts shown are present values calculated using 120% of the AFR (
        {afrDisplay}% × 1.2 = {(afrDisplay * 1.2).toFixed(2)}%), compounded semiannually, per Q&A-31/32.
      </InfoBanner>
      <div className="relative">
        <div
          className={
            shouldMaskResults ? 'space-y-5 blur-[2px] opacity-60 pointer-events-none select-none' : 'space-y-5'
          }
        >
          <div className="grid grid-cols-4 gap-3">
            <StatCard label="Total DQIs" value={results.length} icon={Users} />
            <StatCard label="Under Threshold" value={safeCount} icon={CheckCircle} />
            <StatCard label="Over Threshold" value={excessCount} icon={AlertTriangle} />
            <StatCard label="Total PV Parachute" value={fmt(totalParachute)} icon={DollarSign} />
          </div>
          <Card padding={false}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <Th>Name</Th>
                    <Th>Base Amt</Th>
                    <Th>Severance (PV)</Th>
                    <Th>CIC</Th>
                    <Th>Equity</Th>
                    <Th>Buyer Eq</Th>
                    <Th>Comp Inc</Th>
                    <Th>Other</Th>
                    <Th className="bg-violet-50/50">Parachute PV</Th>
                    <Th className="bg-emerald-50/50">Threshold</Th>
                    <Th>Difference</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.map((r) => (
                    <tr key={r.id}>
                      <Td highlight>{r.name}</Td>
                      <Td>{fmt(r.base)}</Td>
                      <Td>
                        {r.severance > 0 ? fmt(r.severance) : '—'}
                        {r.severance > 0 && r.severanceNominal !== r.severance && (
                          <span className="text-xs text-gray-400 block">({fmt(r.severanceNominal)} nominal)</span>
                        )}
                      </Td>
                      <Td>{r.cic > 0 ? fmt(r.cic) : '—'}</Td>
                      <Td>{r.accelEquity > 0 ? fmt(r.accelEquity) : '—'}</Td>
                      <Td>{r.buyerEquityAwards > 0 ? fmt(r.buyerEquityAwards) : '—'}</Td>
                      <Td>{r.increases > 0 ? fmt(r.increases) : '—'}</Td>
                      <Td>{r.otherPayments > 0 ? fmt(r.otherPayments) : '—'}</Td>
                      <Td className="bg-violet-50/30 font-medium text-violet-700">
                        {fmt(r.parachute)}
                        {r.hasPVDiscount && (
                          <span className="text-xs text-gray-400 block">({fmt(r.parachuteNominal)} nominal)</span>
                        )}
                      </Td>
                      <Td className="bg-emerald-50/30 text-emerald-700">{fmt(r.threshold)}</Td>
                      <Td className={`font-medium ${r.difference >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmt(r.difference)}
                      </Td>
                      <Td>
                        <Badge size="table" variant={r.hasIssue ? 'danger' : 'success'}>
                          {r.hasIssue ? 'Over Threshold' : 'Under Threshold'}
                        </Badge>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <InfoBanner>
            <strong>Understanding the Threshold Test:</strong> If total parachute payment PV is less than 3× base
            amount, none of the payments are subject to Section 280G. If exceeded, the individual faces a 20% excise tax
            on excess amounts.
          </InfoBanner>
          {excessCount > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-5">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={18} />
                <div>
                  <h4 className="text-xs font-medium text-red-900">280G Issue — Shareholder Approval Required</h4>
                  <p className="text-xs text-red-800 mt-1">
                    {excessCount} individual(s) exceed the parachute payment threshold ({fmt(totalExcess)} total
                    excess).
                  </p>
                </div>
              </div>
            </div>
          )}
          {excessCount === 0 && results.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-5">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-emerald-600 flex-shrink-0" size={18} />
                <div>
                  <h4 className="text-xs font-medium text-emerald-900">Under Parachute Threshold — No 280G Issues</h4>
                  <p className="text-xs text-emerald-700">All individuals are below the 3× base amount threshold.</p>
                </div>
              </div>
            </div>
          )}

          {results.length > 0 && !snapshotMode && (
            <DocumentsTable
              analysisId={currentAnalysis?.id ?? null}
              runId={currentAnalysis?.lastRunId ?? null}
              generationDisabled={analysisLocked || !isFormComplete || isRunStale}
              generationDisabledReason={
                analysisLocked
                  ? 'This analysis is final and cannot regenerate.'
                  : !isFormComplete
                    ? 'Complete the workflow first.'
                    : isRunStale
                      ? 'Run 280G Results again after the latest workflow changes.'
                      : undefined
              }
            />
          )}
        </div>
        {shouldMaskResults ? (
          <div className="absolute inset-0 z-10 rounded-lg bg-white/25 border border-gray-200 flex items-start justify-center pt-24">
            <div className="px-3 py-1.5 rounded-md bg-red-50 border border-red-200 text-xs font-medium text-red-900 inline-flex items-center gap-1.5">
              <AlertTriangle size={14} className="text-red-700" />
              {lastRunLabel ? (
                <>
                  Workflow changed. Click <span className="font-semibold">Run 280G Results</span> to refresh these
                  results.
                </>
              ) : (
                <>
                  No run recorded yet. Click <span className="font-semibold">Run 280G Results</span> to record a run and
                  view these results.
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
